import { APIError, NetworkError } from '../api/apiRequest';
import {
  CROSS_PARTITION_LOGIN_NOTICE,
  isCrossPartitionLoginError,
} from '../api/authErrors';
import * as authStore from '../stores/authStore';
import { StudioModalName } from '../stores/navigationStore';
import * as navigationStore from '../stores/navigationStore';
import * as noticesStore from '../stores/noticesStore';
import { getHomepageUrl } from './getHomepageUrl';

export default async function handleGenericErrors(
  error: APIError | NetworkError,
  config?: {
    redirectTo?: string;
    suppressNotices?: boolean;
  },
) {
  if (error instanceof APIError) {
    // Cross-partition logins arrive as a 403 but are not a missing session, so
    // they must not fall through to the login prompt and redirect below: this
    // host can never authenticate the account.
    if (isCrossPartitionLoginError(error)) {
      if (!config?.suppressNotices) {
        noticesStore.error(CROSS_PARTITION_LOGIN_NOTICE);
      }
    } else if (error.status === 401 || error.status === 403) {
      const { authStatus, userID } = authStore.state;
      const sessionRevision = authStore.getAuthSessionRevision();
      const isAuthenticated = authStatus === 'user';

      // The authorizer does not expose an expired-session code for its 403s.
      // Never infer an invalid session from a generic permission denial.
      if (isAuthenticated && error.status === 403) {
        if (!config?.suppressNotices) {
          noticesStore.error(
            error.message ||
              'You do not have permission to perform this action.',
          );
        }
      } else {
        if (!config?.suppressNotices) {
          noticesStore.warning(
            isAuthenticated
              ? 'Your session is no longer valid. Please log in again.'
              : 'Please sign up or login to continue!',
          );
        }

        if (config?.redirectTo) {
          // Capture the destination before logout changes auth state. Encode
          // the whole return target so its query/hash stays inside redirectTo.
          const destination = getHomepageUrl(
            `/${isAuthenticated ? 'login' : 'sign-up'}?redirectTo=${encodeURIComponent(config.redirectTo)}`,
          );
          await new Promise((resolve) => setTimeout(resolve, 2000));
          if (
            authStore.getAuthSessionRevision() !== sessionRevision ||
            authStore.state.authStatus !== authStatus ||
            authStore.state.userID !== userID
          ) {
            return;
          }

          if (isAuthenticated) {
            // Wait for cookie cleanup, but don't let logout's default '/' race
            // the login destination. A newer auth action also cancels navigation.
            const loggingOut = authStore.logout({ redirect: false });
            const logoutRevision = authStore.getAuthSessionRevision();
            await loggingOut;
            if (authStore.getAuthSessionRevision() !== logoutRevision) return;
          }
          window.location.href = destination;
        }
      }
    } else {
      if (!config?.suppressNotices) {
        if (
          error?.message?.toLowerCase?.().includes('service unavailable') ||
          error?.message?.toLowerCase?.().includes('gateway timeout') ||
          error?.message?.toLowerCase?.().includes('request timed out')
        ) {
          noticesStore.error(
            'Your request timed out. Please try again or modify your request.',
          );
        } else if (error.status === 503 || error.status === 504) {
          noticesStore.error(
            'The server encountered an error. Please try again or come back later.',
          );
        } else if (
          error?.message.includes('Insufficient spins available for')
        ) {
          navigationStore.setModal(StudioModalName.READY_FOR_MORE_SPINS);
        } else {
          noticesStore.error(error.message);
        }
      }
    }
  } else if (error instanceof NetworkError) {
    if (!config?.suppressNotices) {
      noticesStore.error('Something went wrong, please try again later!');
    }
  }

  console.log('ERROR:', error);
}
